import { SELECT_MAX_ITEMS } from 'Constants/index'
import debounce from 'lodash.debounce'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilState, useRecoilValue } from 'recoil'

import {
  Chip,
  ChipIconTimes,
  ChipIconWrapper,
  Flex,
  FlexItem,
  FormElement,
  HelpIcon,
  Select,
  SELECT_VARIANT_WITH_CHECKBOX,
  Text,
  TEXT_VARIANT_BOLD,
  Toggle,
  TooltipUtils,
  useAsyncSelect
} from 'mailup-web-components'

import { fetchLists } from 'Pages/SourceEdit/recoil/actions/asyncSelectActions'
import { modalSharedListsState } from 'Pages/SourceEdit/recoil/state/modals'
import { listState } from 'Pages/SourceEdit/recoil/state/source'

const InputLists = () => {
  const intl = useIntl()

  const [{ sharedLists, selectAll, blockedLists }, setModal] = useRecoilState(
    modalSharedListsState
  )
  const { lists } = useRecoilValue(listState)

  const [error, setError] = useState(false)

  useEffect(() => {
    TooltipUtils.rebuild()
  }, [])

  const handleToggleSelectAll = () => {
    setModal(({ selectAll, ...prev }) => ({
      ...prev,
      selectAll: !selectAll
    }))
  }

  const hints = {
    loading: intl.formatMessage({ id: 'Load.MailupCommon.InputStatusLoading' }),
    moreResults: intl.formatMessage({
      id: 'Load.LandingPage.TooManyLists'
    }),
    noResults: intl.formatMessage({
      id: 'Load.MailupCommon.InputStatusNoMatches'
    })
  }
  const getInitialItems = () => fetchLists()
  const getItemsByTerm = (searchTerm) => fetchLists(searchTerm)
  const getItemById = () => {}

  const onError = () => {
    setError(true)
  }

  const { onSearch, onChange, options, ...props } = useAsyncSelect({
    initialValue: sharedLists,
    getInitialItems,
    getItemsByTerm,
    getItemById,
    onError,
    hints,
    maxItems: SELECT_MAX_ITEMS
  })

  useEffect(() => {
    const filterList = sharedLists.filter((value) => value !== 0)
    setModal((prev) => ({ ...prev, sharedLists: filterList }))
  }, [selectAll])

  const handleChange = (v) => {
    const filterList = v.filter((value) => value !== 0)
    onChange(filterList)
    setModal((prev) => ({ ...prev, sharedLists: filterList }))
  }

  const { current: debouncedOnSearch } = useRef(debounce(onSearch, 350))

  const handleSearch = (s) => {
    debouncedOnSearch.cancel()
    debouncedOnSearch(s)
    return s
  }

  const withDisabled = useMemo(() => {
    const options = lists.map((list) => {
      const disabled = blockedLists.includes(list.value)
      return {
        ...list,
        disabled
      }
    })

    return options
  }, [lists, sharedLists, blockedLists])

  return (
    <>
      <Flex
        width='100%'
        justifyContent='space-between'
        alignItems='baseline'
        margin='24px 0 4px 0'
      >
        <FlexItem>
          <Flex>
            <Text variant={TEXT_VARIANT_BOLD} htmlElement='span'>
              {intl.formatMessage({
                id: 'Load.ContentsSouces.SharedModalDdlLabel'
              })}
            </Text>
            <HelpIcon
              text={intl.formatMessage({
                id: 'Load.ContentsSouces.TooltipShare'
              })}
            />
          </Flex>
        </FlexItem>
        <FlexItem>
          <Flex columnGap='8px'>
            <Toggle
              checked={selectAll}
              onChange={handleToggleSelectAll}
              variant='sm'
            />
            <Text>
              {intl.formatMessage({
                id: 'Load.ContentsSouces.SharedModalSelectAll'
              })}
            </Text>
          </Flex>
        </FlexItem>
      </Flex>
      <FormElement>
        <Select
          multiple
          variant={SELECT_VARIANT_WITH_CHECKBOX}
          searchable
          options={withDisabled}
          value={sharedLists}
          disabled={selectAll}
          placeholder={intl.formatMessage({
            id: 'Load.ContentsSouces.SharedModalDdlPlaceHolder'
          })}
          onChange={handleChange}
          onSearch={handleSearch}
          error={error}
          SelectedItemComponent={SelectedItemComponent}
          {...props}
        />
      </FormElement>
    </>
  )
}

export default InputLists

const SelectedItemComponent = ({ label, value, onClick }) => {
  const { blockedLists } = useRecoilValue(modalSharedListsState)

  return (
    <Chip label={label} value={value}>
      {!blockedLists.includes(value) && (
        <ChipIconWrapper
          onClick={(e) => onClick(value, e)}
          data-mu-test='source-edit__modal-shared-lists__remove'
        >
          <ChipIconTimes />
        </ChipIconWrapper>
      )}
    </Chip>
  )
}

SelectedItemComponent.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  onClick: PropTypes.func
}
